import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
// Three.js imports kept for future 3D mode — canvas replaced by RoomEditor2D for now.
// import { Canvas } from '@react-three/fiber';
// import { Grid, MapControls, useTexture } from '@react-three/drei';
import { RoomEditor2D } from '../components/RoomEditor2D';
import type { WizardType, DeviceCombo } from '../store/wizardStore';
import { useWizardStore } from '../store/wizardStore';
import { useComponents, useCatalogSearch, type SywComponent } from '../hooks/useSywData';
import { useMediaQuery } from '../hooks/useMediaQuery';
import { StepperNav } from '../components/StepperNav';
import { EspelhoDeviceWizard } from '../components/EspelhoDeviceWizard';
import { SpaceAnnotationEditor } from '../components/SpaceAnnotationEditor';
import {
  friendlyName,
  teclasOf,
  topSuggestions,
  searchCatalog,
  isCustomOrderEspelho,
} from '../lib/catalogModel';
import {
  pricingOf,
  usePricing,
  mergePricing,
  formatPrice,
  formatInstall,
  type SkuPricing,
} from '../services/pricing';

// ─── Three.js scene (disabled — replaced by RoomEditor2D; kept for future 3D) ─
// ProductSprite, PlaceholderBox, ComponentBillboard, Scene commented out.
// Re-enable by restoring the @react-three imports above and this block.

// ─── Catalog row ─────────────────────────────────────────────────────────────

function ComponentRow({
  comp,
  remote,
  selected,
  onToggle,
}: {
  comp: SywComponent;
  remote?: SkuPricing;
  selected: boolean;
  onToggle: (sku: string) => void;
}) {
  const pricing = mergePricing(pricingOf(comp), remote);
  const install = formatInstall(pricing.installMinutes);
  return (
    <li>
      <button
        type="button"
        onClick={() => onToggle(comp.sku)}
        className={[
          'w-full px-4 py-3 text-left transition-colors',
          selected ? 'bg-blue-50' : 'hover:bg-gray-50 active:bg-gray-100',
        ].join(' ')}
      >
        <div className="flex items-center gap-3">
          {comp.imageUrl ? (
            <img
              src={comp.imageUrl}
              alt=""
              loading="lazy"
              className="h-12 w-12 shrink-0 rounded-lg border border-smartify-line bg-white object-contain"
            />
          ) : (
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-smartify-line bg-smartify-paper text-lg">
              🔌
            </div>
          )}
          <div className="min-w-0 flex-1">
            <p className="line-clamp-2 text-sm font-medium leading-snug text-smartify-ink">
              {friendlyName(comp)}
            </p>
            {isCustomOrderEspelho(comp, pricing.priceEur, remote?.isCustomOrder) ? (
              <div className="mt-0.5 space-y-0.5">
                <p className="text-xs text-smartify-muted">
                  {formatPrice(pricing.priceEur)}
                  {comp.colorName ? ` · ${comp.colorName}` : ''}
                  {' · '}
                  <span className="text-amber-600 font-medium">~5-8 semanas</span>
                </p>
                <span className="inline-block rounded bg-amber-50 px-1 py-px text-[10px] font-semibold text-amber-700 leading-tight">
                  Personalizado por encomenda
                </span>
              </div>
            ) : (
              <p className="mt-0.5 text-xs text-smartify-muted">
                {formatPrice(pricing.priceEur)}
                {install ? ` · ${install}` : ''}
                {comp.colorName ? ` · ${comp.colorName}` : ''}
              </p>
            )}
          </div>
          <span
            className={[
              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold transition-colors',
              selected
                ? 'bg-smartify-brand text-white'
                : 'border border-smartify-line bg-white text-smartify-muted',
            ].join(' ')}
            aria-hidden
          >
            {selected ? '✓' : '+'}
          </span>
        </div>
      </button>
    </li>
  );
}

// ─── Component picker (full width on mobile, sidebar on md+) ────────────────

// "O que tens na parede?" quick filters. Each runs a server-side Shopify search
// (name OR SKU) via its `term`; tecla chips additionally narrow by key count.
const WALL_FILTERS: { key: string; label: string; term: string; teclas?: number }[] = [
  { key: 't1', label: '1 tecla', term: 'interruptor', teclas: 1 },
  { key: 't2', label: '2 teclas', term: 'interruptor', teclas: 2 },
  { key: 't3', label: '3 teclas', term: 'interruptor', teclas: 3 },
  { key: 'tomada', label: 'Tomada', term: 'tomada' },
  { key: 'estores', label: 'Estores', term: 'estore' },
  { key: 'cortinas', label: 'Cortinas', term: 'cortina' },
];

function ComponentPicker({
  catalog,
  searchPool,
  loading,
  selectedSkus,
  combos,
  onToggle,
  onOpenAddDevice,
  onOpenBrowse,
  onRemoveCombo,
  onUpdateComboQty,
  onSplitCombo,
}: {
  catalog: SywComponent[];
  searchPool: SywComponent[];
  loading: boolean;
  selectedSkus: string[];
  combos: DeviceCombo[];
  onToggle: (sku: string) => void;
  onOpenAddDevice: () => void;
  onOpenBrowse: () => void;
  onRemoveCombo: (comboId: string) => void;
  onUpdateComboQty: (comboId: string, qty: number) => void;
  onSplitCombo: (comboId: string) => void;
}) {
  const [search, setSearch] = useState('');
  const [wallKey, setWallKey] = useState<string | null>(null);

  const textSearching = search.trim().length > 0;
  // A wall chip contributes a keyword search; the free-text box takes priority.
  const wall = WALL_FILTERS.find((w) => w.key === wallKey) ?? null;
  const query = search.trim() || (wall?.term ?? '');
  const searching = query.length > 0;

  // SKU + name search runs server-side against Shopify (covers the full 1000+
  // catalog and works without the SYW KV worker / Storefront token).
  const { results: remoteResults, loading: searchLoading } = useCatalogSearch(query);

  const visible = useMemo(() => {
    if (!searching) return catalog;
    // Shopify results first; fold in any local searchPool matches for resilience.
    const local = searchCatalog(searchPool, query);
    const bySku = new Map<string, SywComponent>();
    for (const c of [...remoteResults, ...local]) if (!bySku.has(c.sku)) bySku.set(c.sku, c);
    let rows = [...bySku.values()];
    if (wall?.teclas != null) rows = rows.filter((c) => teclasOf(c) === wall.teclas);
    return rows;
  }, [searching, query, wall, remoteResults, searchPool, catalog]);

  const suggestions = useMemo(
    () => (searching ? [] : topSuggestions(searchPool, 6)),
    [searching, searchPool],
  );

  const listTitle = searching ? (wall ? wall.label : 'Resultados') : 'Catálogo';

  // Authoritative prices (hybrid Shopify/Moloni rule) for the rows on screen;
  // catalog-row values remain the fallback while loading / offline.
  const remotePricing = usePricing(
    [...suggestions, ...visible].map((c) => c.sku),
  );

  return (
    <div className="flex h-full min-h-0 flex-col bg-white md:border-l md:border-smartify-line">
      {/* Search + quick-pick */}
      <div className="space-y-3 border-b border-smartify-line px-4 py-3">
        <button
          type="button"
          onClick={onOpenAddDevice}
          className="w-full rounded-smartify border-2 border-dashed border-smartify-brand/50 px-4 py-2.5 text-sm font-semibold text-smartify-brand transition-colors hover:border-smartify-brand hover:bg-blue-50"
        >
          + Adicionar interruptor (guiado)
        </button>
        {/* Q34: dynamic catalog as the prominent 2nd option, right after the
            guided interruptor wizard. Becomes the default browse source once the
            Shopify Storefront token is configured. */}
        <button
          type="button"
          onClick={onOpenBrowse}
          className="w-full rounded-smartify bg-smartify-ink px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
        >
          🛍️ Explorar catálogo completo (todas as marcas)
        </button>
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Pesquisar no catálogo…"
          aria-label="Pesquisar no catálogo"
          className="w-full rounded-smartify border border-smartify-line px-3 py-2 text-sm focus:border-smartify-brand"
        />
        {!textSearching && (
          <div>
            <p className="mb-1.5 text-xs font-medium text-smartify-muted">
              O que tens na parede?
            </p>
            <div className="flex flex-wrap gap-2">
              {WALL_FILTERS.map((w) => {
                const active = wallKey === w.key;
                return (
                  <button
                    key={w.key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setWallKey(active ? null : w.key)}
                    className={[
                      'rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                      active
                        ? 'border-smartify-brand bg-smartify-brand text-white'
                        : 'border-smartify-line bg-white text-smartify-ink hover:border-smartify-accent',
                    ].join(' ')}
                  >
                    {w.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Lists */}
      {loading || (searching && searchLoading && visible.length === 0) ? (
        <div className="flex flex-1 items-center justify-center text-sm text-smartify-muted">
          {searching ? 'A pesquisar…' : 'A carregar…'}
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto pb-2">
          {combos.length > 0 && !searching && (
            <>
              <h3 className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
                Aparelhos configurados
              </h3>
              <ul className="divide-y divide-smartify-line border-b border-smartify-line">
                {combos.map((combo) => {
                  const qty = combo.qty ?? 1;
                  return (
                  <li key={combo.id} className="flex items-center gap-2 px-4 py-2.5">
                    <span className="text-lg shrink-0" aria-hidden>
                      {combo.kind === 'tomada' ? '🔌' : '💡'}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-smartify-ink">{combo.label}</p>
                      <p className="text-xs text-smartify-muted">
                        {combo.skus.length} {combo.skus.length === 1 ? 'artigo' : 'artigos'}
                        {combo.skus.length > 1 && (
                          <>
                            {' · '}
                            <button
                              type="button"
                              onClick={() => onSplitCombo(combo.id)}
                              className="text-smartify-brand hover:underline"
                            >
                              Separar
                            </button>
                          </>
                        )}
                      </p>
                    </div>
                    {/* Qty stepper */}
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        aria-label="Diminuir quantidade"
                        onClick={() => qty <= 1 ? onRemoveCombo(combo.id) : onUpdateComboQty(combo.id, qty - 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-smartify-line text-sm text-smartify-muted hover:border-smartify-danger hover:text-smartify-danger"
                      >
                        −
                      </button>
                      <span className="w-5 text-center text-sm font-semibold text-smartify-ink">
                        {qty}
                      </span>
                      <button
                        type="button"
                        aria-label="Aumentar quantidade"
                        onClick={() => onUpdateComboQty(combo.id, qty + 1)}
                        className="flex h-7 w-7 items-center justify-center rounded-full border border-smartify-line text-sm text-smartify-muted hover:border-smartify-brand hover:text-smartify-brand"
                      >
                        +
                      </button>
                    </div>
                  </li>
                  );
                })}
              </ul>
            </>
          )}

          {suggestions.length > 0 && (
            <>
              <h3 className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
                Top sugestões
              </h3>
              <ul className="divide-y divide-smartify-line border-b border-smartify-line">
                {suggestions.map((comp) => (
                  <ComponentRow
                    key={`sug-${comp.sku}`}
                    comp={comp}
                    remote={remotePricing[comp.sku]}
                    selected={selectedSkus.includes(comp.sku)}
                    onToggle={onToggle}
                  />
                ))}
              </ul>
            </>
          )}

          <h3 className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-wide text-smartify-muted">
            {listTitle}
          </h3>
          {visible.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-smartify-muted">
              {searching
                ? 'Nada encontrado. Tenta outra palavra (ex.: "interruptor", "tomada").'
                : 'Nenhum componente disponível. Adiciona componentes no painel SYW.'}
            </p>
          ) : (
            <ul className="divide-y divide-smartify-line">
              {visible.map((comp) => (
                <ComponentRow
                  key={comp.sku}
                  comp={comp}
                  remote={remotePricing[comp.sku]}
                  selected={selectedSkus.includes(comp.sku)}
                  onToggle={onToggle}
                />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Page ───────────────────────────────────────────────────────────────────

export function DivisionEditorPage() {
  const { type } = useParams<{ type: WizardType }>();
  const navigate = useNavigate();
  const { activeDivisionId, divisions, selections, devices, roomDecor, toggleSelection, addDevice, removeDevice, updateDeviceQty, ungroupDevice } =
    useWizardStore();

  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [tab, setTab] = useState<'lista' | 'planta'>('lista');
  const [showAddDevice, setShowAddDevice] = useState(false);
  const [addDeviceMode, setAddDeviceMode] = useState<'guided' | 'browse'>('guided');

  const activeDiv = divisions.find((d) => d.id === activeDivisionId);
  const selectedSkus = activeDivisionId ? (selections[activeDivisionId] ?? []) : [];
  const activeCombos = activeDivisionId ? (devices[activeDivisionId] ?? []) : [];

  // P0-4: running subtotal for this division (material, VAT-incl). Prices come
  // LIVE; when some are still "sob consulta" we flag the subtotal as partial
  // rather than hiding it.
  // Combos + standalone products coexist in a division: combo SKUs are
  // mirrored into `selections`, so standalone = selections minus combo SKUs.
  // Counting only one of the two silently dropped the other from the subtotal.
  const divSkuQty = useMemo(() => {
    const m = new Map<string, number>();
    const comboSkus = new Set<string>();
    for (const c of activeCombos) {
      const q = c.qty ?? 1;
      for (const s of c.skus) {
        m.set(s, (m.get(s) ?? 0) + q);
        comboSkus.add(s);
      }
    }
    for (const s of selectedSkus) {
      if (!comboSkus.has(s)) m.set(s, (m.get(s) ?? 0) + 1);
    }
    return m;
  }, [activeCombos, selectedSkus]);
  const divPricing = usePricing([...divSkuQty.keys()]);
  const { divSubtotal, divAllPriced } = useMemo(() => {
    let divSubtotal = 0;
    let divAllPriced = divSkuQty.size > 0;
    for (const [sku, qty] of divSkuQty) {
      const p = divPricing[sku]?.priceEur ?? null;
      if (p == null || p <= 0) divAllPriced = false;
      else divSubtotal += p * qty;
    }
    return { divSubtotal, divAllPriced };
  }, [divSkuQty, divPricing]);

  // Space-annotation step (#5) lives BEFORE products. Show the optional entry
  // gate only on a fresh division (no markers + no products yet); returning to
  // an in-progress division jumps straight to products.
  const decorCount = activeDivisionId ? (roomDecor[activeDivisionId]?.length ?? 0) : 0;
  const [view, setView] = useState<'gate' | 'space' | 'products'>(
    () => (decorCount > 0 || selectedSkus.length > 0 ? 'products' : 'gate'),
  );

  // Category catalog (Shopify w/ SYW fallback) + full SYW list so the search
  // covers the whole catalog, not just this division's category.
  const { data: catalog, loading } = useComponents(activeDiv?.categoryId);
  const { data: fullCatalog } = useComponents(undefined);

  const searchPool = useMemo(() => {
    const m = new Map<string, SywComponent>();
    for (const c of [...catalog, ...fullCatalog]) if (!m.has(c.sku)) m.set(c.sku, c);
    return [...m.values()];
  }, [catalog, fullCatalog]);

  const componentMap = useMemo(
    () => new Map(searchPool.map((c) => [c.sku, c])),
    [searchPool],
  );

  function handleToggle(sku: string) {
    if (!activeDivisionId) return;
    toggleSelection(activeDivisionId, sku);
  }

  function handleSave() {
    navigate(`/wizard/${type}`);
  }

  // Canvas is a desktop nicety — on mobile it only mounts in the "Planta" tab
  // so the list gets the full viewport and we skip the WebGL cost entirely.
  const showCanvas = isDesktop || tab === 'planta';
  const showPicker = isDesktop || tab === 'lista';

  // ── Optional space-annotation gate (#5) ──────────────────────────────────
  if (view === 'gate') {
    return (
      <div className="flex h-screen flex-col">
        <header className="flex items-center gap-3 border-b border-smartify-line bg-white px-4 py-2">
          <button type="button" onClick={handleSave} aria-label="Voltar às divisões"
            className="-ml-1 p-1 text-lg leading-none text-smartify-muted hover:text-smartify-ink">←</button>
          <h1 className="truncate text-sm font-semibold text-smartify-ink">{activeDiv ? activeDiv.label : 'Divisão'}</h1>
        </header>
        <div className="grid flex-1 place-items-center p-6">
          <div className="w-full max-w-sm space-y-4 text-center">
            <div className="text-4xl">🛋️🔌</div>
            <h2 className="text-lg font-semibold text-smartify-ink">Queres desenhar o espaço primeiro?</h2>
            <p className="text-sm text-smartify-muted">
              Marca a mobília e <b>o que já existe</b> (tomadas, AC, alarme, câmaras…) para te
              orçamentarmos melhor. Demora ~1 minuto e é opcional.
            </p>
            <button type="button" onClick={() => setView('space')}
              className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90">
              ✏️ Desenhar o espaço
            </button>
            <button type="button" onClick={() => setView('products')}
              className="w-full rounded-smartify border border-smartify-line px-6 py-3 text-sm font-medium text-smartify-ink hover:border-smartify-brand">
              Ir direto aos produtos Smartify →
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'space' && activeDivisionId) {
    return (
      <div className="flex h-screen flex-col">
        <header className="flex items-center gap-3 border-b border-smartify-line bg-white px-4 py-2">
          <button type="button" onClick={() => setView('gate')} aria-label="Voltar"
            className="-ml-1 p-1 text-lg leading-none text-smartify-muted hover:text-smartify-ink">←</button>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-sm font-semibold text-smartify-ink">Desenhar o espaço · {activeDiv?.label ?? 'Divisão'}</h1>
            <p className="text-xs text-smartify-muted">Mobília + o que já existe (para orçamentar)</p>
          </div>
          <button type="button" onClick={() => setView('products')}
            className="rounded-smartify bg-smartify-brand px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90">
            Produtos →
          </button>
        </header>
        <div className="min-h-0 flex-1">
          <SpaceAnnotationEditor divisionId={activeDivisionId} />
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen flex-col">
      {/* Top bar */}
      <header className="border-b border-smartify-line bg-white">
        <div className="flex items-center gap-3 px-4 py-2">
          <button
            type="button"
            onClick={handleSave}
            aria-label="Voltar às divisões"
            className="-ml-1 p-1 text-lg leading-none text-smartify-muted hover:text-smartify-ink"
          >
            ←
          </button>
          <div className="min-w-0">
            <h1 className="truncate text-sm font-semibold text-smartify-ink">
              {activeDiv ? activeDiv.label : 'Divisão'}
            </h1>
            <p className="text-xs text-smartify-muted">
              {selectedSkus.length}{' '}
              {selectedSkus.length === 1 ? 'componente' : 'componentes'}
              {decorCount > 0 ? ` · ${decorCount} no espaço` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setView('space')}
            className="ml-auto rounded-smartify border border-smartify-line px-2.5 py-1.5 text-xs font-medium text-smartify-ink hover:border-smartify-brand"
          >
            ✏️ Espaço
          </button>
          <div className="hidden items-center gap-3 md:flex">
            {divSkuQty.size > 0 && (
              <span className="text-sm text-smartify-muted">
                Subtotal{divAllPriced ? '' : ' parcial'}:{' '}
                <span className="font-semibold text-smartify-ink">
                  {divSubtotal > 0 ? `${divAllPriced ? '' : 'desde '}${formatPrice(divSubtotal)}` : 'sob consulta'}
                </span>
              </span>
            )}
            <button
              type="button"
              onClick={handleSave}
              className="rounded-smartify bg-smartify-brand px-4 py-1.5 text-sm font-medium text-white hover:opacity-90"
            >
              Guardar
            </button>
          </div>
        </div>

        <StepperNav current={2} />

        {/* Mobile tabs: list first, plan preview opt-in */}
        <div className="flex border-t border-smartify-line md:hidden" role="tablist">
          {(
            [
              ['lista', 'Componentes'],
              ['planta', 'Posicionar'],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={tab === key}
              onClick={() => setTab(key)}
              className={[
                'flex-1 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors',
                tab === key
                  ? 'border-smartify-brand text-smartify-brand'
                  : 'border-transparent text-smartify-muted',
              ].join(' ')}
            >
              {label}
            </button>
          ))}
        </div>
      </header>

      {/* Main: single column on mobile, canvas + sidebar on md+ */}
      <div className="flex min-h-0 flex-1">
        {showCanvas && activeDivisionId && (
          <div className="min-h-0 flex-1">
            <RoomEditor2D
              divisionId={activeDivisionId}
              selectedSkus={selectedSkus}
              combos={devices[activeDivisionId] ?? []}
              componentMap={componentMap}
            />
          </div>
        )}

        {showPicker && (
          <div className="flex min-h-0 w-full flex-col md:w-96 md:shrink-0">
            <ComponentPicker
              catalog={catalog}
              searchPool={searchPool}
              loading={loading}
              selectedSkus={selectedSkus}
              combos={activeDivisionId ? (devices[activeDivisionId] ?? []) : []}
              onToggle={handleToggle}
              onOpenAddDevice={() => { setAddDeviceMode('guided'); setShowAddDevice(true); }}
              onOpenBrowse={() => { setAddDeviceMode('browse'); setShowAddDevice(true); }}
              onRemoveCombo={(comboId) => {
                if (activeDivisionId) removeDevice(activeDivisionId, comboId);
              }}
              onUpdateComboQty={(comboId, qty) => {
                if (activeDivisionId) updateDeviceQty(activeDivisionId, comboId, qty);
              }}
              onSplitCombo={(comboId) => {
                if (activeDivisionId) ungroupDevice(activeDivisionId, comboId);
              }}
            />
          </div>
        )}
      </div>

      {showAddDevice && activeDivisionId && (
        <EspelhoDeviceWizard
          catalog={searchPool}
          initialMode={addDeviceMode}
          onSave={(combo) => addDevice(activeDivisionId, combo)}
          onClose={() => setShowAddDevice(false)}
        />
      )}

      {/* Mobile bottom action bar */}
      <div className="border-t border-smartify-line bg-white px-4 py-3 md:hidden">
        {divSkuQty.size > 0 && (
          <div className="mb-2 flex items-center justify-between text-sm">
            <span className="text-smartify-muted">Subtotal divisão{divAllPriced ? '' : ' (parcial)'}</span>
            <span className="font-semibold text-smartify-ink">
              {divSubtotal > 0 ? `${divAllPriced ? '' : 'desde '}${formatPrice(divSubtotal)}` : 'sob consulta'}
            </span>
          </div>
        )}
        <button
          type="button"
          onClick={handleSave}
          className="w-full rounded-smartify bg-smartify-brand px-6 py-3 font-semibold text-white hover:opacity-90"
        >
          Guardar{selectedSkus.length > 0 ? ` (${selectedSkus.length})` : ''}
        </button>
      </div>
    </div>
  );
}
